import { MouseEvent, useCallback, useMemo, useState } from 'react';
import {
  CheckCircleOutlined,
  Close,
  Delete,
  Edit,
  HowToReg,
  Mail,
  MoreHoriz,
  RemoveCircleOutline,
} from '@mui/icons-material';
import { DialogContent, IconButton, Paper, Popper, Tooltip } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { userSelectedProfileUrl } from 'common/config/urls';
import { HAS_NOT_LOGGED_IN_YET } from 'common/constants/user';
import { AuthService } from 'services';
import { useResponse } from 'services/hooks';
import { useRemoveUserFromFirm } from 'services/hooks/useManageUser/useManageUser';
import { useUpdateUserProfile } from 'services/hooks/useUserSelected';
import { Logger, SHORT_DATE_FORMAT } from 'utilities';
import { UserRowProps } from './UserRow.props';
import { StyledUserTableCell, StyledUserTableRow, userRowClasses } from './UserRow.styles';

const UserRow = ({
  user,
  setCurrentUser,
  isSuperUser,
  firm,
  isFirmAdmin,
  togglePrimaryUser,
  is409a,
  currentUser,
}: UserRowProps) => {
  const { successNotification, errorNotification } = useResponse();
  const [openActionsDialog, setOpenActionsDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const { email, last_login: lastLogin, suspended_at } = user;
  const isSuspended = Boolean(suspended_at);
  const navigate = useNavigate();
  const { mutate: removeUserFromFirm } = useRemoveUserFromFirm();
  const { mutate: activateUser } = useUpdateUserProfile();

  const authService = AuthService.getInstance();

  const handleMoreClick = (event: MouseEvent<HTMLButtonElement>) => {
    setOpenActionsDialog(!openActionsDialog);
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleDialogClose = useCallback(() => {
    setOpenActionsDialog(false);
    setAnchorEl(null);
  }, []);

  const goToUserSettings = () => {
    if (firm?.slug && user.id) {
      const selectedUserSettings = userSelectedProfileUrl(firm.slug, user.id);
      navigate(selectedUserSettings);
    }
  };

  const deleteUser = async () => {
    if (firm?.id && user.id) {
      removeUserFromFirm(
        {
          firm_id: firm.id,
          user_id: user.id,
        },
        {
          onSuccess: () => {
            successNotification('User was removed from the firm successfully');
            handleDialogClose();
            setCurrentUser();
          },
          onError: () => {
            errorNotification('An error occurred while removing the user from firm');
          },
        }
      );
    }
  };

  const activateUserHandler = useCallback(async () => {
    if (user.id) {
      activateUser(
        {
          userId: user.id,
          updatedUserProfile: {
            username: user.username,
            email: user.email,
            is_active: !user.is_active,
          },
        },
        {
          onSuccess: () => {
            successNotification(`User was ${user.is_active ? 'disabled' : 'activated'} successfully`);
            handleDialogClose();
          },
          onError: () => {
            errorNotification('An error occurred while activating the user');
          },
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activateUser, user]);

  const resendPasswordResetOrActivation = useCallback(async () => {
    if (user.email) {
      try {
        if (user.is_active) {
          await authService.sendPasswordReset({ email: user.email });
          successNotification('Password reset email was sent successfully');
        } else {
          await authService.resendActivationEmail(user.email);
          successNotification('Activation email was sent successfully');
        }
      } catch (error) {
        errorNotification('An error occurred while resending the email');
        Logger.error(`Failed to resend email for user ${user.email}`, error);
      } finally {
        handleDialogClose();
      }
    }
  }, [authService, errorNotification, handleDialogClose, successNotification, user]);

  const onTogglePrimaryUser = useCallback(() => {
    if (togglePrimaryUser) {
      togglePrimaryUser(user).then(() => {
        handleDialogClose();
      });
    }
  }, [user, togglePrimaryUser, handleDialogClose]);

  const isSelected = currentUser?.id === user.id;
  
  const resetPasswordOrActivateActionTooltipMessage = useMemo(() => {
    if (isSuspended) {
      return 'User account has been suspended, revert the suspension by activating the user to enable this action';
    }
    return user.is_active ? 'Send password reset' : 'Resend account activation';
  }, [user.is_active, isSuspended]);

  return (
    <StyledUserTableRow
      onClick={() => setCurrentUser(user)}
      style={{
        backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
        borderLeft: isSelected ? '4px solid #2196f3' : 'none',
      }}>
      <StyledUserTableCell className={`userRowClasses.tableCell ${!user.is_active ? userRowClasses.inactiveUser : ''}`}>
        {email}
      </StyledUserTableCell>
      <StyledUserTableCell className={`userRowClasses.tableCell ${!user.is_active ? userRowClasses.inactiveUser : ''}`}>
        {lastLogin ? format(parseISO(lastLogin), SHORT_DATE_FORMAT) : HAS_NOT_LOGGED_IN_YET}
      </StyledUserTableCell>
      {(isSuperUser || isFirmAdmin) && (
        <StyledUserTableCell className={userRowClasses.tableCell}>
          <Tooltip title="More">
            <IconButton aria-label="more" onClick={handleMoreClick}>
              <MoreHoriz />
            </IconButton>
          </Tooltip>
          <Popper open={openActionsDialog} anchorEl={anchorEl} placement="top">
            <Paper>
              <DialogContent>
                {isSuperUser && (
                  <Tooltip title={user.is_active ? 'Disable user' : 'Activate user'}>
                    <IconButton aria-label="delete" onClick={activateUserHandler}>
                      {user.is_active ? <RemoveCircleOutline /> : <HowToReg />}
                    </IconButton>
                  </Tooltip>
                )}
                {is409a && togglePrimaryUser && (
                  <Tooltip title="Set As Primary Contact">
                    <IconButton aria-label="primary_user" onClick={onTogglePrimaryUser}>
                      <CheckCircleOutlined {...(user.is_primary_contact && { color: 'primary' })} />
                    </IconButton>
                  </Tooltip>
                )}
                {firm && (
                  <Tooltip title={`Edit ${!isSuperUser ? ' is disabled' : ''}`}>
                    <div style={{ display: 'inline' }}>
                      <IconButton aria-label="edit" onClick={goToUserSettings} disabled={!isSuperUser}>
                        <Edit />
                      </IconButton>
                    </div>
                  </Tooltip>
                )}
                {firm && (
                  <Tooltip title="Remove from firm">
                    <IconButton aria-label="delete" onClick={deleteUser}>
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title={resetPasswordOrActivateActionTooltipMessage}>
                  <span>
                    <IconButton aria-label="resend" onClick={resendPasswordResetOrActivation} disabled={isSuspended}>
                      <Mail />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Close">
                  <IconButton aria-label="close" onClick={handleDialogClose} className={userRowClasses.closeButton}>
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              </DialogContent>
            </Paper>
          </Popper>
        </StyledUserTableCell>
      )}
    </StyledUserTableRow>
  );
};

export default UserRow;
